import api from './api';

// ============================================================
// Tipos
// ============================================================

export interface WAGTenantConfig {
  plano: 'FREE' | 'BASIC' | 'PRO' | 'ENTERPRISE';
  maxAgencias: number;
  maxServicos: number;
  maxOperadores: number;
  smsEnabled: 'S' | 'N';
  displayLogo?: string;
  displayCorFundo: string;
  displayCorTexto: string;
  displayCorDestaque: string;
}

export interface WAGAgencia {
  id: string;
  codigo: string;
  desig: string;
  descr?: string;
  mensagem?: string;
  endereco?: string;
  coordLat?: string;
  coordLng?: string;
  qrToken: string;
  estado: 'A' | 'I';
  totalServicos: number;
  totalBalcoes: number;
}

export interface WAGServico {
  id: string;
  codigo: string;
  desig: string;
  sigla: string;
  descr?: string;
  urlLogo?: string;
  tempoMedio: number;
  prioridade: 'S' | 'N';
  estado: 'A' | 'I';
}

export interface WAGAgServico {
  id: string;
  desig: string;
  sigla: string;
  urlLogo?: string;
  tempoMedio: number;
  prioridade: 'S' | 'N';
  agServicoId: string;
  ordem: number;
  maxSenhaDia?: number;
  horaInicio?: string;
  horaFim?: string;
  estado: 'A' | 'I';
}

export interface WAGBalcao {
  id: string;
  numero: number;
  desig: string;
  estado: 'A' | 'I';
  operadorActivo?: string;
}

export interface WAGOperador {
  id: string;
  userId: string;
  papel: 'ADMIN' | 'SUPERVISOR' | 'OPERADOR';
  dtInicio: string;
  dtFim?: string;
  estado: 'A' | 'I';
  balcaoDesig?: string;
  balcaoNumero?: number;
  nomeUtilizador?: string;
  email?: string;
}

export interface WAGStats {
  data: string;
  servico: string;
  sigla: string;
  totalSenhas: number;
  totalAtendidos: number;
  totalDesistencias: number;
  totalPrioritarios: number;
  tempoEsperaMedioMin: number;
  tempoAtendMedioMin: number;
}

export interface WAGDashboard {
  totalAgencias: number;
  totalServicos: number;
  senhasHoje: number;
  atendidosHoje: number;
  desistenciasHoje: number;
  tempoEsperaMedioMin: number;
  plano: string;
}

// ============================================================
// Service
// ============================================================

class WAGService {
  // ---- Config do tenant ----
  async getConfig(): Promise<WAGTenantConfig> {
    const res = await api.get('/admin/wag/config');
    return res.data.data;
  }

  async updateConfig(data: Partial<WAGTenantConfig>): Promise<void> {
    await api.put('/admin/wag/config', {
      displayLogo: data.displayLogo,
      displayCorFundo: data.displayCorFundo,
      displayCorTexto: data.displayCorTexto,
      displayCorDestaque: data.displayCorDestaque,
    });
  }

  // ---- Agências ----
  async getAgencias(): Promise<WAGAgencia[]> {
    const res = await api.get('/admin/wag/agencias');
    return res.data.data;
  }

  async createAgencia(data: Partial<WAGAgencia>): Promise<{ id: string; qrToken: string }> {
    const res = await api.post('/admin/wag/agencias', data);
    return res.data.data;
  }

  async updateAgencia(id: string, data: Partial<WAGAgencia>): Promise<void> {
    await api.put(`/admin/wag/agencias/${id}`, data);
  }

  // ---- Serviços ----
  async getServicos(): Promise<WAGServico[]> {
    const res = await api.get('/admin/wag/servicos');
    return res.data.data;
  }

  async createServico(data: Partial<WAGServico>): Promise<{ id: string }> {
    const res = await api.post('/admin/wag/servicos', data);
    return res.data.data;
  }

  async updateServico(id: string, data: Partial<WAGServico>): Promise<void> {
    await api.put(`/admin/wag/servicos/${id}`, data);
  }

  // ---- Serviços de uma agência ----
  async getAgenciaServicos(agenciaId: string): Promise<WAGAgServico[]> {
    const res = await api.get(`/admin/wag/agencias/${agenciaId}/servicos`);
    return res.data.data;
  }

  async associarServico(agenciaId: string, data: {
    servicoId: string; ordem?: number; maxSenhaDia?: number;
    horaInicio?: string; horaFim?: string;
  }): Promise<void> {
    await api.post(`/admin/wag/agencias/${agenciaId}/servicos`, data);
  }

  // ---- Balcões ----
  async getBalcoes(agenciaId: string): Promise<WAGBalcao[]> {
    const res = await api.get(`/admin/wag/agencias/${agenciaId}/balcoes`);
    return res.data.data;
  }

  async createBalcao(agenciaId: string, data: { numero: number; desig: string }): Promise<void> {
    await api.post(`/admin/wag/agencias/${agenciaId}/balcoes`, data);
  }

  // ---- Operadores ----
  async getOperadores(agenciaId: string): Promise<WAGOperador[]> {
    const res = await api.get(`/admin/wag/agencias/${agenciaId}/operadores`);
    return res.data.data;
  }

  async addOperador(agenciaId: string, data: { userId: string; papel?: string; balcaoId?: string }): Promise<void> {
    await api.post(`/admin/wag/agencias/${agenciaId}/operadores`, data);
  }

  // ---- Estatísticas ----
  async getStats(agenciaId: string, dias = 7): Promise<WAGStats[]> {
    const res = await api.get(`/admin/wag/agencias/${agenciaId}/stats?dias=${dias}`);
    return res.data.data;
  }

  // ---- Dashboard global ----
  async getDashboard(): Promise<WAGDashboard | null> {
    const res = await api.get('/admin/wag/dashboard');
    return res.data.data ?? null;
  }
}

export default new WAGService();
